import {
  EDIT_ACTIVE_MEDICATION,
  LOAD_ACTIVE_MEDICATION_START,
  LOAD_ACTIVE_MEDICATION_SUCCESS,
  MedicationAction,
  NEW_MEDICATION_PREFILL_SUCCESS,
  REMOVE_MEDICATION,
  REPEAT_MED_PREFILL_START,
  REPEAT_MED_PREFILL_SUCCESS,
  RESET_ACTIVE_MEDICATION,
  SELECT_ACTIVE_MEDICATION,
  STOP_MEDICATION_SUCCESS,
  TOGGLE_SELECT_MEDICATION,
} from "../types/medication.types";
import {
  MedicationEntryPrescriptionStatusEnum,
  MedicationEntryType,
  sortMedicationEntries,
} from "model/medication-entry.model";
import { CLOSE_PATIENT, PatientAction } from "../types/patient.types";
import _ from "lodash";
import { PrescriptionType } from "model/prescription.model";
import {
  PrescriptionAction,
  PRESCRIPTIONS_CREATED,
  PRESCRIPTIONS_CREATED_R,
  PRESCRIPTIONS_CREATED_T,
  PRESCRIPTIONS_PDF,
} from "store/types/prescription.types";
import { PrescriptionStatusType } from "common/enums/PrescriptionStatusTypeEnum";
import { CONTACT_CREATE_START, ContactAction } from "store/types/contact.types";
import { HelenaAction, PRESCRIPTIONS_HELENA } from "store/types/helena.types";
import moment from "moment";
import { StatusTypeEnum } from "common/enums/StatusTypeEnum";

export interface MedicationState {
  /** Current status of all the entries */
  medicationEntries: Array<MedicationEntryType>;
  /** Medication being edited */
  activeMedicationEntry?: number;
  /** Entries selected for create/stop */
  selectedMedicationEntries: Array<number>;
  /** Created prescriptions */
  prescriptions: Array<PrescriptionType>;
  /** Stopped medications */
  stoppedMedications: Array<number>;
}

export const initialMedicationState: MedicationState = {
  activeMedicationEntry: undefined,
  medicationEntries: [],
  selectedMedicationEntries: [],
  prescriptions: [],
  stoppedMedications: [],
};

/** ---- SELECTORS ---- */
export const isStoppedCareConnect = (state: MedicationState): boolean => {
  const toMakePassive = selectMedicationEntriesToCreate(state).filter(
    (e) => e.status !== StatusTypeEnum.ACTIVE
  );
  return toMakePassive.length === state.stoppedMedications.length;
};

export const isPrescriptionSavedCareConnect = (
  state: MedicationState
): boolean => {
  if (
    selectMedicationEntriesToCreate(state).filter(
      (e) => e.status === StatusTypeEnum.ACTIVE
    ).length === 0
  ) {
    return true;
  } else {
    return (
      state.prescriptions.length > 0 &&
      state.prescriptions.every((p) => p.id !== undefined)
    );
  }
};

export const isPrescriptionSavedRecipe = (state: MedicationState): boolean => {
  return (
    state.prescriptions.length > 0 &&
    state.prescriptions.every(
      (p) =>
        p.id !== undefined &&
        p.recipeStatus !== PrescriptionStatusType.NOT_IN_RECIPE
    )
  );
};

export const isPrescriptionSavedHelena = (state: MedicationState): boolean => {
  return (
    state.prescriptions.length > 0 &&
    state.prescriptions.every(
      (p) => p.id !== undefined && p.helenaDate !== undefined
    )
  );
};

export const selectPrescriptions = (
  state: MedicationState
): Array<PrescriptionType> => {
  return state.prescriptions;
};

export const selectMedicationEntries = (
  state: MedicationState
): Array<MedicationEntryType> => {
  return state.medicationEntries;
};

export const selectActiveMedicationEntry = (state: MedicationState) => {
  return state.medicationEntries.filter(
    (value) => value.id === state.activeMedicationEntry
  )[0];
};

export const selectSelectedMedicationEntries = (state: MedicationState) => {
  return state.selectedMedicationEntries;
};

export const selectMedicationEntriesToCreate = (state: MedicationState) => {
  return state.medicationEntries.filter((entry) => {
    return _.indexOf(state.selectedMedicationEntries, entry.id) >= 0;
  });
};

/** ---- ACTION HANDLERS ---- */
const clearMedication = () => {
  return initialMedicationState;
};

const updatePrescriptions = (
  state: MedicationState,
  prescriptions: Array<PrescriptionType>
): MedicationState => ({
  ...state,
  prescriptions: prescriptions,
});

const helenaPrescription = (state: MedicationState): MedicationState => ({
  ...state,
  prescriptions: state.prescriptions.map((p) =>
    Object.assign({} as PrescriptionType, p, {
      helenaDate: moment(),
    })
  ),
});

const clearPrescriptions = (state: MedicationState): MedicationState => ({
  ...state,
  prescriptions: [],
});

const loadActiveMedicationSuccess = (
  state: MedicationState,
  medicationEntries: Array<MedicationEntryType>
): MedicationState => ({
  ...state,
  medicationEntries: medicationEntries,
  selectedMedicationEntries: [],
  stoppedMedications: [],
});

const selectActiveMedication = (
  state: MedicationState,
  payload: MedicationEntryType
): MedicationState => ({
  ...state,
  activeMedicationEntry: payload.id,
  medicationEntries: state.medicationEntries.map((entry) =>
    entry.id === payload.id ? payload : entry
  ),
});

const editActiveMedication = (
  state: MedicationState,
  payload: MedicationEntryType
): MedicationState => ({
  ...state,
  activeMedicationEntry: undefined,
  medicationEntries: state.medicationEntries.map((entry) =>
    entry.id === payload.id ? payload : entry
  ),
});

const resetActiveMedication = (state: MedicationState) => ({
  ...state,
  activeMedicationEntry: undefined,
});

const newMedicationPrefill = (
  state: MedicationState,
  medicationEntry: MedicationEntryType
): MedicationState => {
  const id = newMinId(state.medicationEntries);

  return {
    ...state,
    activeMedicationEntry: id,
    medicationEntries: sortMedicationEntries(
      _.xor(state.medicationEntries, [
        Object.assign({}, medicationEntry, {
          id: id,
          prescriptionStatus: MedicationEntryPrescriptionStatusEnum.NEW,
        }),
      ])
    ),
    selectedMedicationEntries: _.xor(state.selectedMedicationEntries, [id]),
  };
};

const toggleSelectedMedicationEntries = (
  state: MedicationState,
  medicationEntry: MedicationEntryType
) => {
  return {
    ...state,
    selectedMedicationEntries: _.xor(state.selectedMedicationEntries, [
      medicationEntry.id,
    ]),
  };
};

const repeatPrefillStart = (
  state: MedicationState,
  entry: MedicationEntryType
): MedicationState => {
  return {
    ...state,
    medicationEntries: sortMedicationEntries(
      _.concat(
        _.filter(state.medicationEntries, (e) => e.id !== entry.id),
        [
          Object.assign({}, entry, {
            prescriptionStatus: MedicationEntryPrescriptionStatusEnum.PREFILL,
          }),
        ]
      )
    ),
  };
};

const repeatPrefill = (
  state: MedicationState,
  entries: { old: MedicationEntryType; new: MedicationEntryType }
): MedicationState => {
  const id = newMinId(state.medicationEntries);
  const xorSelected = _.xor(state.selectedMedicationEntries, [id]);

  return {
    ...state,
    selectedMedicationEntries: xorSelected,
    medicationEntries: sortMedicationEntries(
      _.concat(
        _.filter(
          state.medicationEntries,
          (entry) => entry.id !== entries.old.id
        ),
        [
          Object.assign({}, entries.new, {
            id: id,
            prescriptionStatus: MedicationEntryPrescriptionStatusEnum.REPEAT,
          }),
        ]
      )
    ),
  };
};

const stopMedication = (
  state: MedicationState,
  entries: Array<MedicationEntryType>
): MedicationState => {
  return {
    ...state,
    stoppedMedications: entries.map((entry) => entry.id),
  };
};

const removeMedication = (
  state: MedicationState,
  medicationEntry: MedicationEntryType
): MedicationState => {
  return {
    ...state,
    selectedMedicationEntries: _.filter(
      state.selectedMedicationEntries,
      (e) => e !== medicationEntry.id
    ),
    medicationEntries: _.filter(
      state.medicationEntries,
      (e) => medicationEntry.id !== e.id
    ),
  };
};

/** ---- UTILS ---- */
const newMinId = (entries: Array<MedicationEntryType>): number => {
  return (_.min(entries.map((m) => m.id).filter((id) => id < 0)) || 0) - 1;
};

/** ---- REDUCER ---- */
export const medicationReducer = (
  state = initialMedicationState,
  action:
    | MedicationAction
    | PatientAction
    | PrescriptionAction
    | ContactAction
    | HelenaAction
): MedicationState => {
  switch (action.type) {
    case CLOSE_PATIENT:
      return clearMedication();
    case LOAD_ACTIVE_MEDICATION_SUCCESS:
      return loadActiveMedicationSuccess(state, action.payload);
    case SELECT_ACTIVE_MEDICATION:
      return selectActiveMedication(state, action.payload);
    case EDIT_ACTIVE_MEDICATION:
      return editActiveMedication(state, action.payload);
    case RESET_ACTIVE_MEDICATION:
      return resetActiveMedication(state);
    case TOGGLE_SELECT_MEDICATION:
      return toggleSelectedMedicationEntries(state, action.payload);
    case REPEAT_MED_PREFILL_SUCCESS:
      return repeatPrefill(state, action.payload);
    case REPEAT_MED_PREFILL_START:
      return repeatPrefillStart(state, action.payload);
    case NEW_MEDICATION_PREFILL_SUCCESS:
      return newMedicationPrefill(state, action.payload);
    case CONTACT_CREATE_START:
    case LOAD_ACTIVE_MEDICATION_START:
      return clearPrescriptions(state);
    case PRESCRIPTIONS_HELENA:
      return helenaPrescription(state);
    case PRESCRIPTIONS_CREATED:
    case PRESCRIPTIONS_CREATED_R:
    case PRESCRIPTIONS_CREATED_T:
    case PRESCRIPTIONS_PDF:
      return updatePrescriptions(state, action.payload);
    case STOP_MEDICATION_SUCCESS:
      return stopMedication(state, action.payload.old);
    case REMOVE_MEDICATION:
      return removeMedication(state, action.payload);
    default:
      return state;
  }
};
